/**
 * Logger Service
 * 
 * Provides centralized logging functionality with different log levels
 * and environment-aware behavior.
 */

// Log levels in order of severity
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

class LoggerService {
  private readonly isDev: boolean;
  private logLevel: LogLevel;

  constructor() {
    // Determine if we're in development mode
    this.isDev = import.meta.env.DEV === true;
    
    // Set default log level based on environment
    this.logLevel = this.isDev ? LogLevel.DEBUG : LogLevel.WARN;
  }

  /**
   * Set the minimum log level
   * @param level The minimum log level to display
   */
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  /**
   * Log a debug message (only in development)
   * @param message The message to log
   * @param optionalParams Additional parameters to log
   */
  debug(message: string, ...optionalParams: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(`[DEBUG] ${message}`, ...optionalParams);
    }
  }

  /**
   * Log an informational message
   * @param message The message to log
   * @param optionalParams Additional parameters to log
   */
  info(message: string, ...optionalParams: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(`[INFO] ${message}`, ...optionalParams);
    }
  }

  /**
   * Log a warning message
   * @param message The message to log
   * @param optionalParams Additional parameters to log
   */
  warn(message: string, ...optionalParams: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[WARN] ${message}`, ...optionalParams);
    }
  }

  /**
   * Log an error message
   * @param message The message to log
   * @param optionalParams Additional parameters to log
   */
  error(message: string, ...optionalParams: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`[ERROR] ${message}`, ...optionalParams);
    }
  }

  /**
   * Determine if a message at the given level should be logged
   * @param level The log level to check
   * @returns True if the message should be logged
   */
  private shouldLog(level: LogLevel): boolean {
    return this.isDev || level >= this.logLevel;
  }
}

// Export singleton instance
export const logger = new LoggerService();

// Also export as default for compatibility with JavaScript imports
export default logger;
